<template>
  <main class="statistics-page" @keydown.esc="copyMenuVisible = false">
    <div v-if="verify" class="statistics-content">
      <header class="page-header">
        <div>
          <h1>报名统计</h1>
          <p class="update-time">更新于 {{ formattedDate }}</p>
        </div>
        <div class="header-actions">
          <van-button class="icon-button" icon="replay" :loading="loading" :disabled="loading"
            aria-label="刷新数据" title="刷新数据" @click="loadData" />
          <van-popover v-model="copyMenuVisible" placement="bottom-end" :actions="copyActions" :get-container="getPopoverContainer"
            @select="selectCopy">
            <template #reference>
              <van-button class="copy-button" icon="description" :disabled="loading || loadError || !updatedAt"
                :aria-expanded="String(copyMenuVisible)" aria-haspopup="menu" @click="copyMenuVisible = !copyMenuVisible">
                复制<van-icon name="arrow-down" />
              </van-button>
            </template>
          </van-popover>
        </div>
      </header>

      <div v-if="loading" class="status-area" role="status">
        <van-loading size="20px" />
        <span>正在查询统计数据</span>
      </div>
      <div v-else-if="loadError" class="status-area" role="alert">
        <van-icon name="warning-o" />
        <span>统计数据加载失败</span>
        <van-button class="retry-button" @click="loadData">重试</van-button>
      </div>
      <template v-else>
        <dl class="summary">
          <div>
            <dt>报名总人数</dt>
            <dd>{{ count(totalData, 'allTotal') }}</dd>
          </div>
          <div class="outside-summary">
            <dt>省外报名</dt>
            <dd>{{ count(totalData, 'outsideProvinceCount') }}</dd>
          </div>
          <div>
            <dt>企业报名</dt>
            <dd>{{ count(totalData, 'companyCount') }}</dd>
          </div>
          <div>
            <dt>工作人员</dt>
            <dd>{{ count(workTotalData, 'allTotal') }}</dd>
          </div>
        </dl>
        <section class="forum-section" aria-labelledby="forum-heading">
          <div class="section-heading">
            <h2 id="forum-heading">分论坛明细</h2>
            <span>{{ guestDataItems.length }} 个分论坛</span>
          </div>
          <table class="forum-table">
            <colgroup><col class="forum-column" /><col /><col /><col /></colgroup>
            <thead>
              <tr><th scope="col">分论坛</th><th scope="col">嘉宾报名</th><th scope="col">审核通过</th><th scope="col">工作人员</th></tr>
            </thead>
            <tbody>
              <tr v-for="item in guestDataItems" :key="item.key">
                <th scope="row">{{ item.label }}</th>
                <td>{{ count(totalData, item.key) }}</td>
                <td class="approved-count">{{ count(totalData.successCounts, item.key) }}</td>
                <td>{{ count(workTotalData, item.key) }}</td>
              </tr>
              <tr v-if="!guestDataItems.length">
                <td colspan="4" class="empty-state">当前活动暂无分论坛</td>
              </tr>
            </tbody>
          </table>
        </section>
        <p class="data-note">总报名及省外人数含全部人员类型；省外指湖南省外，不含地区未填。</p>
      </template>
    </div>

    <form v-else class="login-form" @submit.prevent="verifyPassword">
      <van-icon name="bar-chart-o" class="login-icon" />
      <h1>报名统计</h1>
      <label for="statistics-password">访问密码</label>
      <input id="statistics-password" v-model="password" type="password" autocomplete="current-password"
        placeholder="请输入访问密码" required />
      <van-button class="login-button" native-type="submit" block>查看数据</van-button>
    </form>
  </main>
</template>

<script>
import qs from "qs";
import { Toast } from "vant";

export default {
  data() {
    return {
      verify: false,
      password: "",
      loading: false,
      loadError: false,
      updatedAt: "",
      totalData: {},
      workTotalData: {},
      copyMenuVisible: false,
      copyActions: [
        { text: "复制报名数据", key: "guest" },
        { text: "复制审核数据", key: "audit" },
        { text: "复制工作人员数据", key: "work" }
      ],
    };
  },
  computed: {
    miceLink() {
      return this.$route.query.miceLink || this.$store.state.miceInfo.miceLink || "";
    },
    guestDataItems() {
      return (this.totalData.groups || [])
        .filter(item => item && item.groupLink)
        .map(item => ({ key: item.groupLink, label: item.name || item.groupLink }));
    },
    formattedDate() {
      return this.updatedAt || "--";
    }
  },
  methods: {
    getPopoverContainer() {
      return this.$el;
    },
    selectCopy(action) {
      this.copyMenuVisible = false;
      if (action.key === "guest") this.copyTotalContext();
      else if (action.key === "audit") this.copyAuditContext();
      else if (action.key === "work") this.copyWorkTotalContext();
    },
    verifyPassword() {
      if (this.password !== "2025") {
        Toast.fail("密码错误，请重试");
        return;
      }
      this.verify = true;
      this.loadData();
    },
    count(data, key) {
      return data && data[key] != null ? data[key] : "--";
    },
    async loadData() {
      if (this.loading) return;
      this.loading = true;
      this.loadError = false;
      try {
        const params = this.miceLink ? { miceLink: this.miceLink } : {};
        const [guest, work] = await Promise.all([
          this.$http.get(`/api/backstageApply/statistics?${qs.stringify(params)}`, 1),
          this.$http.get(`/api/backstageApply/statistics?${qs.stringify({ ...params, type: 1 })}`, 1)
        ]);
        if (!guest || guest.code !== 200 || !guest.data || !Array.isArray(guest.data.groups)
          || !work || work.code !== 200 || !work.data) {
          throw new Error("统计数据加载失败");
        }
        this.totalData = guest.data;
        this.workTotalData = work.data;
        this.updatedAt = this.$moment().format("YYYY-MM-DD HH:mm");
      } catch (error) {
        this.loadError = true;
        Toast.fail("统计数据加载失败，请重试");
      } finally {
        this.loading = false;
      }
    },
    async copyTotalContext() {
      const items = this.guestDataItems.map(item =>
        `${item.label}：(${this.count(this.totalData, item.key)})`
      ).join('\n');
      const text = `截止${this.formattedDate}嘉宾报名数据：
${items}
企业总报名数：(${this.count(this.totalData, 'companyCount')})
报名总人数：(${this.count(this.totalData, 'allTotal')})
省外报名人数：(${this.count(this.totalData, 'outsideProvinceCount')})
`;
      try {
        await navigator.clipboard.writeText(text);
        Toast.success("嘉宾报名数据已复制到剪贴板！");
      } catch {
        Toast.fail("复制失败，请重试");
      }
    },
    async copyWorkTotalContext() {
      const items = this.guestDataItems.map(item =>
          `${item.label}：${this.count(this.workTotalData, item.key)}`
      ).join('\n');

      const text = `截止${this.formattedDate}工作人员报名数据：
${items}
工作人员总数：${this.count(this.workTotalData, 'allTotal')}`;

      try {
        await navigator.clipboard.writeText(text);
        Toast.success("工作人员数据已复制到剪贴板！");
      } catch {
        Toast.fail("复制失败，请重试");
      }
    },
    async copyAuditContext() {
      const items = this.guestDataItems.map(item =>
          `${item.label}：（审核通过 ${this.count(this.totalData.successCounts, item.key)} / 总报名 ${this.count(this.totalData, item.key)}）`
      ).join('\n');

      const text = `截止${this.formattedDate}嘉宾审核数据：
${items}`;

      try {
        await navigator.clipboard.writeText(text);
        Toast.success("审核数据已复制到剪贴板！");
      } catch {
        Toast.fail("复制失败，请重试");
      }
    },
  },
};
</script>

<style scoped>
.statistics-page {
  box-sizing: border-box;
  min-height: 100vh;
  padding: 40Px 24Px calc(24Px + env(safe-area-inset-bottom));
  background: #fff;
  color: #25282d;
  font-size: 14Px;
  line-height: 1.5;
  letter-spacing: 0;
}
.statistics-content {
  max-width: 880Px;
  margin: 0 auto;
}
.page-header, .header-actions, .section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12Px;
}
.statistics-page h1 {
  margin: 0;
  font-size: 22Px;
  font-weight: 600;
  line-height: 1.4;
}
.update-time {
  margin: 5Px 0 0;
  color: #85898f;
  font-size: 12Px;
}
.header-actions { gap: 8Px; flex-shrink: 0; }
.statistics-page .van-button {
  height: 34Px;
  padding: 0 12Px;
  border: 1Px solid #e2e4e7;
  border-radius: 4Px;
  background: #fff;
  color: #42474e;
  font-size: 13Px;
  line-height: 1;
}
.statistics-page .icon-button {
  width: 34Px;
  padding: 0;
}
.copy-button .van-icon { margin-left: 7Px; font-size: 11Px; }
.summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 28Px 0 26Px;
  padding: 22Px 0;
  border-top: 1Px solid #e8eaed;
  border-bottom: 1Px solid #e8eaed;
  gap: 16Px;
}
.summary dt { font-size: 12Px; color: #777d84; }
.summary dd {
  margin: 6Px 0 0;
  font-size: 28Px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
.outside-summary dd { color: #16776d; }
.section-heading { margin-bottom: 12Px; }
.section-heading h2 { margin: 0; font-size: 14Px; font-weight: 600; }
.section-heading span { font-size: 12Px; color: #85898f; }
.forum-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.forum-column { width: 46%; }
.forum-table th, .forum-table td {
  padding: 12Px 10Px;
  border-bottom: 1Px solid #f0f1f3;
  text-align: right;
  font-size: 14Px;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
.forum-table thead th { background: #f6f7f8; color: #777d84; font-size: 12Px; }
.forum-table th:first-child { text-align: left; }
.forum-table tbody tr:hover { background: #fafbfc; }
.forum-table .approved-count { color: #16776d; }
.forum-table .empty-state { padding: 40Px 12Px; color: #85898f; text-align: center; }
.data-note { margin: 16Px 0 0; color: #85898f; font-size: 11Px; line-height: 1.8; }
.status-area { display: flex; justify-content: center; align-items: center; gap: 10Px; padding: 80Px 0; color: #777d84; }
.login-form { max-width: 320Px; margin: 12vh auto 0; }
.login-icon { font-size: 28Px; color: #16776d; margin-bottom: 16Px; }
.login-form label { display: block; margin: 28Px 0 8Px; font-size: 13Px; color: #777d84; }
.login-form input {
  box-sizing: border-box;
  width: 100%;
  height: 44Px;
  padding: 0 12Px;
  border: 1Px solid #dfe2e5;
  border-radius: 4Px;
  background: #fff;
  font-size: 16Px;
  color: #25282d;
}
.login-form input:focus { outline: 2Px solid #16776d; outline-offset: 2Px; }
.statistics-page .login-button { margin-top: 16Px; height: 42Px; background: #25282d; color: #fff; border-color: #25282d; }
/* Keep this dashboard independent of the site's viewport-based rem scaling. */
.statistics-page ::v-deep .van-popover__action { width: 174Px; height: 42Px; padding: 0 16Px; font-size: 13Px; }
.statistics-page ::v-deep .van-popover__content { border-radius: 6Px; }
.statistics-page ::v-deep .van-popover__arrow { border-width: 6Px; }
.statistics-page ::v-deep .van-button__icon { font-size: 16Px; }
.statistics-page ::v-deep .van-button__loading { width: 16Px; height: 16Px; }
@media (max-width: 540px) {
  .statistics-page { padding: 24Px 16Px calc(24Px + env(safe-area-inset-bottom)); }
  .statistics-page h1 { font-size: 20Px; }
  .summary { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20Px 16Px; margin-top: 22Px; padding: 20Px 0; }
  .summary dd { font-size: 26Px; }
  .forum-column { width: 40%; }
  .forum-table th, .forum-table td { padding: 12Px 5Px; font-size: 13Px; }
  .forum-table thead th { font-size: 11Px; }
  .header-actions { gap: 6Px; }
}
</style>
